import { defineStore } from 'pinia'
import { tc4OperatingHoursToString } from '@/utils/swoop'
import { getAccessType, TECH_TYPE } from '@/data/products'
import { sortTC4First } from '@/utils/sortList'
import { toAVCID } from '@/utils/avc'

export const useQuoteStore = defineStore('quote', {
  state: () => ({
    currentStep: 'installation_location',
    quoteOrderId: null,
    location: null,
    sfProducts: null,
    sfLocation: null,
    sqProducts: [],
    products: [],
    calculatedProductResponse: null,
    quoteName: null,
    reference: null,
    contacts: [],
    isFormDirty: false,
    currentQuote: null,
    terms: null,
    isCalculating: false,
    acceptedAll: false,
    tempProducts: {},
    isPortCopperValid: true,
    currentService: null,
    transferOption: null,
    requiresAppointment: false,
    selectNextAvailablePort: false,
    subsequentInstallationChargeApplies: false,
    selfInstallNTDOption: false,
    isSiteContactRequired: false
  }),
  getters: {
    totalMRC: state => {
      return state.products
        ?.map(product => parseFloat(product?.pricing?.monthly) || 0)
        ?.reduce((acc, curr) => acc + curr, 0)
    },

    totalInstallFee: state => {
      return state.products
        ?.map(
          product =>
            parseFloat(product?.pricing?.setup) +
              parseFloat(product?.pricing?.build_cost) || 0
        )
        ?.reduce((acc, curr) => acc + curr, 0)
    },

    getProductPayload(state) {
      const newProducts = state.products.map(product => {
        let newProduct = {
          ...product,
          service_type: product.productType === 'DIA' ? 'internet' : 'ethernet',
          sla: product.configuration?.SLA__c,
          request_date: new Date().toISOString(),
          notes: '',
          options_map: {
            ...product.options_map,
            productObject: product,
            serviceabilityClass: parseInt(
              product.options_map?.serviceabilityClass
            ) // ensures that serviceabilityClass is passed as int specially for older quotes before SQ v3
          },
          original_service_id: this.currentService?.id || null
        }

        if (product.group?.includes('NBN TC4')) {
          const isFC = product?.connectionType === 'fibre_connect'

          newProduct = {
            ...newProduct,
            access_type:
              product?.access_type === TECH_TYPE.FTTP || isFC
                ? TECH_TYPE.FTTP
                : product?.access_type,
            service_type: product?.productType,
            connectionType: isFC ? 'Fibre Connect' : product?.connectionType,
            additional_info: {
              ...newProduct.additional_info,
              is_nbn_fc: isFC
            }
          }

          if (product.additional_info?.operating_hours) {
            newProduct = {
              ...newProduct,
              additional_info: {
                ...newProduct.additional_info,
                operating_hours: tc4OperatingHoursToString(
                  product.additional_info?.operating_hours
                )
              }
            }
          }

          if (isFC) {
            newProduct = {
              ...newProduct,
              options_map: {
                ...newProduct.options_map,
                accessType: product?.productType,
                leadTime: product?.leadTime
              }
            }
          }
        }

        return newProduct
      })

      return newProducts
    },

    // payload when creating/saving a sf quote
    getQuotePayload(state) {
      const payload = {
        quote_name: state.quoteName,
        reference: state.reference || '',
        opportunity_reference: state.location?.address,
        sq_location_object: state.sfLocation,
        contacts: state.contacts,
        sq_products_object: state.sfProducts,
        products: this.getProductPayload
      }

      return payload
    },

    getNewServices(state) {
      const sortServices = sortTC4First(state.currentQuote?.services, 'product')
      const newServices = sortServices?.map((service, i) => {
        let newService = service

        const product = state.products?.find(
          product => product.group === service.options_map?.productObject?.group
        )

        const newOptionsMap = {
          ...service.options_map,
          ...product.options_map
        }

        const circuitId =
          newOptionsMap?.circuit_id &&
          this.isPortCopperValid &&
          this.transferOption === 'service_transfer'
            ? toAVCID(newOptionsMap?.circuit_id)
            : null

        const operatingHours = tc4OperatingHoursToString(
          product.additional_info?.operating_hours
        )

        if (product) {
          newService = {
            ...newService,
            additional_info: {
              ...product.additional_info,
              operating_hours_formatted: operatingHours
            },
            options_map: {
              ...newOptionsMap,
              operating_hours_formatted: operatingHours,
              circuit_id: circuitId,
              connect_outstanding:
                newOptionsMap?.transfer_option === 'connect_outstanding',
              isCircuitIDValid: this.isPortCopperValid,
              // self_install_ntd_option: this.selfInstallNTDOption,
              serviceabilityClass: parseInt(
                product.options_map?.serviceabilityClass
              )
            },
            circuit_id: circuitId
          }
        }

        if (i === 0) {
          newService = {
            ...newService,
            contacts: state.contacts
          }
        }

        return newService
      })

      return newServices
    },

    // payload when updating quote questionnaire
    getUpdateQuotePayload(state) {
      return {
        ...state.currentQuote,
        services: this.getNewServices
      }
    },

    // payload when updating sf quote line item
    getQuoteLineItemUpdatePayload() {
      // first service should be TC4 from sorting
      const firstService = this.getNewServices[0]

      if (!firstService) return null

      const siteContact = firstService?.contacts?.find(
        contact => !contact.email && !contact?.external_system_id
      )

      return {
        id: firstService?.id,
        site_contact_firstname: siteContact?.first_name,
        site_contact_lastname: siteContact?.last_name,
        site_contact_phone: siteContact?.phone,
        notes: firstService?.additional_info?.additional_site_information,
        circuit_id:
          this.transferOption === 'service_transfer' && firstService?.circuit_id
            ? firstService?.circuit_id
            : null,
        connect_outstanding: this.transferOption === 'connect_outstanding',
        // self_install_ntd_option: this.selfInstallNTDOption,
        update_children: true
      }
    }
  },
  actions: {
    setCurrentStep(step) {
      this.currentStep = step
    },
    setLocation(location) {
      this.location = location
    },
    setSFProducts(products) {
      this.sfProducts = products
    },
    setSFLocation(location) {
      this.sfLocation = location
    },
    setProducts(products) {
      this.products = products
    },
    setCalculatedProductResponse(response) {
      this.calculatedProductResponse = response
    },
    setQuoteOrderID(orderId) {
      this.quoteOrderId = orderId
    },
    setFormIsDirty(state) {
      this.isFormDirty = state
    },
    setCurrentQuote(quote) {
      this.currentQuote = quote
    },
    setTerms(terms) {
      this.terms = terms
    },
    setIsCalculating(state) {
      this.isCalculating = state
    },
    setAcceptedAll(state) {
      this.acceptedAll = state
    },
    setTempProducts(state) {
      this.tempProducts = state
    },
    setCurrentService(service) {
      this.currentService = service
    },
    setCopperPortValidity(state) {
      this.isPortCopperValid = state
    },
    setTransferOption(data) {
      this.transferOption = data
    },
    setRequiresAppointment(state) {
      this.requiresAppointment = state
    },
    setSubsequentInstallationChargeApplies(state) {
      this.subsequentInstallationChargeApplies = state
    },
    setIsSiteContactRequired(state) {
      this.isSiteContactRequired = state
    }
  }
})
